import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Table,
    Form, Badge, Pagination, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { formatCurrency, formatDate, formatNumber } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function Tresorerie() {
    const navigate = useNavigate();
    const [solde, setSolde] = useState(null);
    const [evolution, setEvolution] = useState([]);
    const [operations, setOperations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [periode, setPeriode] = useState('mois');
    const [annee, setAnnee] = useState(new Date().getFullYear());
    const [annees, setAnnees] = useState([]);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        fetchAnnees();
        fetchDonnees();
    }, [periode, annee]);

    const fetchAnnees = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/stats.php?action=annees`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setAnnees(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement années", err);
        }
    };

    const fetchDonnees = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            
            const soldeResponse = await fetch(
                `${API_BASE_URL}/finances.php?action=solde`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            const evolutionResponse = await fetch(
                `${API_BASE_URL}/finances.php?action=evolution&periode=${periode}&annee=${annee}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            const operationsResponse = await fetch(
                `${API_BASE_URL}/finances.php?action=operations&limite=20`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!soldeResponse.ok || !evolutionResponse.ok || !operationsResponse.ok) {
                throw new Error("Erreur chargement des données");
            }
            
            const soldeResult = await soldeResponse.json();
            const evolutionResult = await evolutionResponse.json();
            const operationsResult = await operationsResponse.json();
            
            setSolde(soldeResult.data);
            setEvolution(evolutionResult.data || []);
            setOperations(operationsResult.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const mois = [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentOperations = operations.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(operations.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const maxValeur = evolution.length > 0 
        ? Math.max(...evolution.map(e => Math.max(e.entrees, e.sorties, Math.abs(e.solde || 0))), 1) 
        : 1;

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement de la trésorerie...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchDonnees}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-money-euro-circle-line fs-20 text-primary"></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Trésorerie</h4>
                                    <p className="text-muted small mb-0">Suivi des flux financiers</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/stats/dashboard')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards modernes */}
                    {solde && (
                        <Row className="g-3 mb-4">
                            <Col sm={6} xl={4}>
                                <div className="kpi-card kpi-success">
                                    <div className="kpi-icon">
                                        <i className="ri-arrow-down-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Entrées</span>
                                        <h3 className="kpi-value">{formatCurrency(solde.entrees)}</h3>
                                        <span className="kpi-unit">encaissements reçus</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={4}>
                                <div className="kpi-card kpi-danger">
                                    <div className="kpi-icon">
                                        <i className="ri-arrow-up-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Sorties</span>
                                        <h3 className="kpi-value">{formatCurrency(solde.sorties)}</h3>
                                        <span className="kpi-unit">décaissements effectués</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={4}>
                                <div className={`kpi-card ${solde.solde >= 0 ? 'kpi-primary' : 'kpi-warning'}`}>
                                    <div className="kpi-icon">
                                        <i className={`${solde.solde >= 0 ? 'ri-bank-line' : 'ri-alert-line'} fs-24`}></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Solde</span>
                                        <h3 className="kpi-value">{formatCurrency(solde.solde)}</h3>
                                        <span className="kpi-unit">{solde.solde >= 0 ? 'trésorerie positive' : 'découvert'}</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Filtres modernes */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-end g-3">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-calendar-line me-2"></i>
                                            Période
                                        </Form.Label>
                                        <Form.Select 
                                            value={periode} 
                                            onChange={(e) => setPeriode(e.target.value)}
                                            className="rounded-pill"
                                        >
                                            <option value="mois"> Mensuel</option>
                                            <option value="annee"> Annuel</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                {periode === 'mois' && (
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-muted small text-uppercase">
                                                <i className="ri-calendar-2-line me-2"></i>
                                                Année
                                            </Form.Label>
                                            <Form.Select 
                                                value={annee} 
                                                onChange={(e) => setAnnee(e.target.value)}
                                                className="rounded-pill"
                                            >
                                                {annees.map(a => (
                                                    <option key={a} value={a}>{a}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                )}
                                <Col md={periode === 'mois' ? 4 : 8}>
                                    <Button 
                                        variant="primary" 
                                        onClick={fetchDonnees}
                                        className="w-100 rounded-pill py-2"
                                        style={{ background: '#0ea5e9', border: 'none' }}
                                    >
                                        <i className="ri-refresh-line me-2"></i>Actualiser
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Graphique d'évolution */}
                    {evolution.length > 0 && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4">
                            <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="section-icon-small">
                                        <i className="ri-bar-chart-2-line text-primary"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">
                                        Évolution {periode === 'mois' ? 'mensuelle' : 'annuelle'}
                                    </h6>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-4 pt-0">
                                <div className="graph-container">
                                    <div className="graph-bars-treasury">
                                        {evolution.map((item, index) => {
                                            const hauteurEntrees = maxValeur > 0 ? (item.entrees / maxValeur) * 150 : 0;
                                            const hauteurSorties = maxValeur > 0 ? (item.sorties / maxValeur) * 150 : 0;
                                            
                                            let libelle = '';
                                            if (periode === 'mois') {
                                                const moisIndex = parseInt(item.mois) - 1;
                                                if (!isNaN(moisIndex) && moisIndex >= 0 && moisIndex < mois.length) {
                                                    libelle = mois[moisIndex].substring(0, 3);
                                                } else {
                                                    libelle = `Mois ${item.mois || '?'}`;
                                                }
                                            } else {
                                                libelle = item.annee || 'N/A';
                                            }
                                            
                                            return (
                                                <div key={index} className="graph-bar-item">
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip>
                                                                Entrées: {formatCurrency(item.entrees || 0)}<br/>
                                                                Sorties: {formatCurrency(item.sorties || 0)}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <div className="d-flex justify-content-center gap-1 mb-2">
                                                            <div 
                                                                className="bar-entree"
                                                                style={{ height: `${hauteurEntrees}px` }}
                                                            ></div>
                                                            <div 
                                                                className="bar-sortie"
                                                                style={{ height: `${hauteurSorties}px` }}
                                                            ></div>
                                                        </div>
                                                    </OverlayTrigger>
                                                    <small className="graph-label">{libelle}</small>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="graph-legend mt-4 pt-3 d-flex justify-content-center gap-4">
                                        <div className="d-flex align-items-center gap-1">
                                            <span className="legend-bar" style={{ background: '#10b981' }}></span>
                                            <small>Entrées</small>
                                        </div>
                                        <div className="d-flex align-items-center gap-1">
                                            <span className="legend-bar" style={{ background: '#ef4444' }}></span>
                                            <small>Sorties</small>
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Dernières opérations */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="section-icon-small">
                                        <i className="ri-history-line text-info"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Dernières opérations</h6>
                                </div>
                                <Badge className="px-3 py-2 rounded-pill" style={{ background: '#0ea5e9' }}>
                                    {operations.length} opérations
                                </Badge>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">Date</th>
                                            <th>Type</th>
                                            <th>Référence</th>
                                            <th>Tiers</th>
                                            <th className="text-end pe-4">Montant</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentOperations.map((op, index) => (
                                            <tr key={index} className="operation-row">
                                                <td className="ps-4"><small>{formatDate(op.date)}</small></td>
                                                <td>
                                                    <span className={`status-badge ${op.type === 'Règlement' || op.type === 'Facture' ? 'text-success' : 'text-danger'}`} style={{ background: op.type === 'Règlement' || op.type === 'Facture' ? '#10b98115' : '#ef444415' }}>
                                                        {op.type}
                                                    </span>
                                                </td>
                                                <td><span className="fw-semibold">{op.reference}</span></td>
                                                <td>{op.tiers}</td>
                                                <td className={`text-end pe-4 fw-bold ${op.sens === 'entree' ? 'text-success' : 'text-danger'}`}>
                                                    {op.sens === 'entree' ? '+' : '-'}{formatCurrency(op.montant)}
                                                </td>
                                            </tr>
                                        ))}
                                        {currentOperations.length === 0 && (
                                            <tr>
                                                <td colSpan="5" className="text-center py-5">
                                                    <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                                                    <h5>Aucune opération récente</h5>
                                                    <p className="text-muted">Aucune transaction enregistrée</p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                        
                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                <div className="text-muted small">
                                    Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, operations.length)} sur {operations.length} opérations
                                </div>
                                <Pagination className="mb-0">
                                    <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                    {renderPagination()}
                                    <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                </Pagination>
                            </div>
                        )}
                    </Card>
                </div>
                <Footer />
            </div>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-success { border-left-color: #10b981; }
                .kpi-danger { border-left-color: #ef4444; }
                .kpi-primary { border-left-color: #0ea5e9; }
                .kpi-warning { border-left-color: #f59e0b; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #0ea5e9; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Section Icon Small */
                .section-icon-small {
                    width: 32px;
                    height: 32px;
                    background: #f1f5f9;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                /* Graphique */
                .graph-container {
                    padding: 1rem;
                }
                .graph-bars-treasury {
                    display: flex;
                    align-items: flex-end;
                    justify-content: space-around;
                    min-height: 250px;
                }
                .graph-bar-item {
                    text-align: center;
                    width: 60px;
                }
                .bar-entree {
                    background: #10b981;
                    width: 18px;
                    border-radius: 4px 4px 0 0;
                    transition: all 0.3s ease;
                    cursor: pointer;
                }
                .bar-sortie {
                    background: #ef4444;
                    width: 18px;
                    border-radius: 4px 4px 0 0;
                    transition: all 0.3s ease;
                    cursor: pointer;
                }
                .bar-entree:hover, .bar-sortie:hover {
                    transform: scaleX(1.1);
                }
                .graph-label {
                    font-size: 0.7rem;
                    color: #64748b;
                }
                .legend-bar {
                    width: 16px;
                    height: 4px;
                    border-radius: 2px;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .operation-row {
                    transition: all 0.2s ease;
                }
                
                .operation-row:hover {
                    background: #f8fafc;
                }
                
                /* Pagination */
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: #0ea5e9;
                    border-color: #0ea5e9;
                    color: white;
                    box-shadow: 0 4px 10px rgba(14, 165, 233, 0.3);
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>
        </>
    );
}